import React, { useState, useEffect } from 'react';
import {
  FaTimes,
  FaClock,
  FaSearch,
  FaSpinner,
  FaArrowLeft,
  FaArrowRight,
  FaClipboardList,
  FaConciergeBell,
  FaBox,
  FaUtensils,
  FaCheckCircle,
  FaHourglassHalf,
  FaCheck,
  FaBan,
  FaFire,
  FaPlay,
} from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';

const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

const TrackOrderKitchen = () => {
  const [orders, setOrders] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterDate, setFilterDate] = useState('today');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [selectedTab, setSelectedTab] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    fetchAllOrders();
  }, []);

  const fetchAllOrders = async () => {
    setLoading(true);
    try {
      const roomRes = await fetch(`${BQ_BASE_URL}/bq/api/kitchen/orders`, {
        headers: { accept: 'application/json' },
      });

      const restaurantRes = await fetch(`${BQ_BASE_URL}/bq/api/restaurant/kitchen/orders`, {
        headers: { accept: 'application/json' },
      });

      let roomOrders = [];
      let restaurantOrders = [];

      if (roomRes.ok) {
        const roomData = await roomRes.json();
        roomOrders = roomData || [];
      }

      if (restaurantRes.ok) {
        const restaurantData = await restaurantRes.json();
        restaurantOrders = restaurantData.orders || [];
      }

      const transformedRoomOrders = roomOrders.map((order) => ({
        id: order.order_id,
        kot_number: order.order_id?.slice(0, 8) || 'N/A',
        order_type: 'room',
        identifier: `Room ${order.room_number || '—'}`,
        room_number: order.room_number,
        status: order.status ? order.status.toLowerCase() : 'pending',
        original_status: order.status,
        ordered_at: order.ordered_at,
        items: (order.ordered_items || []).map((item, idx) => ({
          id: `${order.order_id}_item_${idx}`,
          name: item.name || 'Unknown',
          quantity: item.quantity || 1,
          item_status: order.status ? order.status.toLowerCase() : 'pending',
        })),
      }));
      const transformedRestaurantOrders = restaurantOrders.map((order) => {
        let identifier = '';
        if (order.order_type === 'dining') {
          identifier = `Table ${order.table_number || '—'}`;
        } else {
          identifier = `Parcel ${order.parcel_number || '—'}`;
        }

        const orderStatus = order.kot_status || order.order_status;

        return {
          id: order.order_id,
          kot_number: order.kot_number || order.order_id?.slice(0, 8) || 'N/A',
          order_type: order.order_type || 'parcel',
          identifier: identifier,
          status: orderStatus ? orderStatus.toLowerCase() : 'pending',
          original_status: orderStatus,
          ordered_at: order.ordered_at,
          special_request: order.special_request,
          kitchen_notes: order.kitchen_notes,
          items: (order.items || []).map((item) => ({
            id: item.item_id,
            name: item.name || 'Unknown',
            quantity: item.quantity || 1,
            item_status: (item.item_status || orderStatus || 'pending').toLowerCase(),
            special_request: item.special_request,
          })),
        };
      });

      const allOrders = [...transformedRoomOrders, ...transformedRestaurantOrders];
      setOrders(allOrders);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Failed to fetch kitchen orders');
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (id, status, orderType) => {
    try {
      let url;
      if (orderType === 'room') {
        url = `${BQ_BASE_URL}/bq/api/kitchen/orders/${id}/status`;
        const response = await fetch(url, {
          method: 'PATCH',
          headers: {
            accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ status: status }),
        });

        if (response.ok) {
          setOrders((prevOrders) =>
            prevOrders.map((order) =>
              order.id === id
                ? { ...order, previous_status: order.status, status: status.toLowerCase() }
                : order
            )
          );
          toast.success(`Order ${status}!`);
        } else {
          const error = await response.json();
          toast.error(`Failed to update status: ${error.detail || 'Unknown error'}`);
        }
      } else {
        url = `${BQ_BASE_URL}/bq/api/restaurant/orders/${id}/status`;
        const response = await fetch(url, {
          method: 'PATCH',
          headers: {
            accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ order_status: status.toLowerCase() }),
        });

        if (response.ok) {
          setOrders((prevOrders) =>
            prevOrders.map((order) =>
              order.id === id
                ? { ...order, previous_status: order.status, status: status.toLowerCase() }
                : order
            )
          );
          toast.success(`Order ${status}!`);
        } else {
          const error = await response.json();
          toast.error(`Failed to update status: ${error.detail || 'Unknown error'}`);
        }
      }
    } catch (error) {
      console.error('Error updating order status:', error);
      toast.error('An error occurred while updating the status.');
    }
  };
  const updateItemStatus = async (orderId, itemId, newStatus, orderType) => {
    if (orderType === 'room') {
      toast.error('Individual item status update not available for room orders');
      return;
    }

    try {
      const url = `${BQ_BASE_URL}/bq/api/restaurant/orders/${orderId}/items/${itemId}/status`;

      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ item_status: newStatus.toLowerCase() }),
      });

      if (response.ok) {
        setOrders((prevOrders) =>
          prevOrders.map((order) => {
            if (order.id === orderId) {
              const updatedItems = order.items.map((item) =>
                item.id === itemId ? { ...item, item_status: newStatus.toLowerCase() } : item
              );

              const allItemStatuses = updatedItems.map((i) => i.item_status);
              let newOrderStatus = order.status;

              if (allItemStatuses.every((s) => s === 'served' || s === 'cancelled')) {
                newOrderStatus = 'served';
              } else if (
                allItemStatuses.every((s) => s === 'ready' || s === 'served' || s === 'cancelled')
              ) {
                newOrderStatus = 'ready';
              } else if (allItemStatuses.some((s) => s === 'preparing')) {
                newOrderStatus = 'preparing';
              } else if (allItemStatuses.some((s) => s === 'accepted')) {
                newOrderStatus = 'accepted';
              } else if (allItemStatuses.some((s) => s === 'pending')) {
                newOrderStatus = 'pending';
              }

              if (newOrderStatus !== order.status) {
                fetch(`${BQ_BASE_URL}/bq/api/restaurant/orders/${orderId}/status`, {
                  method: 'PATCH',
                  headers: {
                    accept: 'application/json',
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({ order_status: newOrderStatus }),
                }).catch((err) => console.error('Error updating order status:', err));
              }

              return { ...order, items: updatedItems, status: newOrderStatus };
            }
            return order;
          })
        );

        toast.success(`Item marked as ${newStatus}!`);
      } else {
        const error = await response.json();
        toast.error(`Failed to update item: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error updating item status:', error);
      toast.error('An error occurred while updating the item status.');
    }
  };
  const isTodayOrder = (orderDate) => {
    if (!orderDate) return false;
    try {
      const todayStr = new Date().toISOString().split('T')[0];
      const orderDateStr = orderDate.split('T')[0];
      return orderDateStr === todayStr;
    } catch (error) {
      return false;
    }
  };
  const getStatusConfig = (status) => {
    const safeStatus = status ? status.toLowerCase() : 'pending';
    const statusMap = {
      pending: {
        label: 'Pending',
        icon: FaClock,
        color: 'text-amber-600',
        bg: 'bg-amber-50',
        border: 'border-l-amber-500',
      },
      accepted: {
        label: 'Accepted',
        icon: FaCheckCircle,
        color: 'text-blue-600',
        bg: 'bg-blue-50',
        border: 'border-l-blue-500',
      },
      preparing: {
        label: 'Preparing',
        icon: FaHourglassHalf,
        color: 'text-indigo-600',
        bg: 'bg-indigo-50',
        border: 'border-l-indigo-500',
      },
      ready: {
        label: 'Ready',
        icon: FaCheckCircle,
        color: 'text-emerald-600',
        bg: 'bg-emerald-50',
        border: 'border-l-emerald-500',
      },
      completed: {
        label: 'Completed',
        icon: FaCheckCircle,
        color: 'text-gray-600',
        bg: 'bg-gray-50',
        border: 'border-l-gray-400',
      },
      delivered: {
        label: 'Delivered',
        icon: FaCheckCircle,
        color: 'text-gray-600',
        bg: 'bg-gray-50',
        border: 'border-l-gray-400',
      },
      served: {
        label: 'Served',
        icon: FaCheckCircle,
        color: 'text-green-600',
        bg: 'bg-green-50',
        border: 'border-l-green-500',
      },
      cancelled: {
        label: 'Cancelled',
        icon: FaTimes,
        color: 'text-red-600',
        bg: 'bg-red-50',
        border: 'border-l-red-500',
      },
    };
    return statusMap[safeStatus] || statusMap.pending;
  };

  const getItemStatusConfig = (status) => {
    const safeStatus = status ? status.toLowerCase() : 'pending';
    const statusMap = {
      pending: {
        label: 'Pending',
        color: 'text-amber-600',
        bg: 'bg-amber-50',
        nextStatus: 'accepted',
        nextLabel: 'Accept Order',
      },
      accepted: {
        label: 'Accepted',
        color: 'text-blue-600',
        bg: 'bg-blue-50',
        nextStatus: 'preparing',
        nextLabel: 'Start Preparing',
      },
      preparing: {
        label: 'Preparing',
        color: 'text-indigo-600',
        bg: 'bg-indigo-50',
        nextStatus: 'ready',
        nextLabel: 'Mark Ready',
      },
      ready: {
        label: 'Ready',
        color: 'text-emerald-600',
        bg: 'bg-emerald-50',
        nextStatus: 'served',
        nextLabel: 'Mark Served',
      },
      served: {
        label: 'Served',
        color: 'text-green-600',
        bg: 'bg-green-50',
        nextStatus: null,
        nextLabel: null,
      },
      cancelled: {
        label: 'Cancelled',
        color: 'text-red-600',
        bg: 'bg-red-50',
        nextStatus: null,
        nextLabel: null,
      },
    };
    return statusMap[safeStatus] || statusMap.pending;
  };

  // Define which previous statuses a user can revert to from a given status
  const getRevertOptions = (currentStatus, orderType) => {
    const s = (currentStatus || 'pending').toLowerCase();

    const roomRevertMap = {
      accepted: ['pending'],
      preparing: ['accepted', 'pending'],
      ready: ['preparing', 'accepted', 'pending'],
      delivered: ['ready', 'preparing', 'accepted', 'pending'],
      cancelled: ['pending'],
    };

    const restaurantRevertMap = {
      accepted: ['pending'],
      preparing: ['accepted', 'pending'],
      ready: ['preparing', 'accepted', 'pending'],
      served: ['ready', 'preparing', 'accepted', 'pending'],
      cancelled: ['pending'],
    };

    const map = orderType === 'room' ? roomRevertMap : restaurantRevertMap;
    return (map[s] || []).filter(Boolean);
  };

  // Item-level revert (restaurant only)
  const getItemRevertOptions = (currentStatus) => {
    const s = (currentStatus || 'pending').toLowerCase();
    const map = {
      accepted: ['pending'],
      preparing: ['accepted', 'pending'],
      ready: ['preparing', 'accepted', 'pending'],
      served: ['ready', 'preparing', 'accepted', 'pending'],
      cancelled: ['pending'],
    };
    return (map[s] || []).filter(Boolean);
  };

  const getTypeIcon = (type) => {
    const icons = {
      room: FaConciergeBell,
      dining: FaUtensils,
      parcel: FaBox,
    };
    return icons[type] || FaConciergeBell;
  };

  const getTypeLabel = (type) => {
    const labels = {
      room: 'Room',
      dining: 'Dine In',
      parcel: 'Parcel',
    };
    return labels[type] || type;
  };

  const filteredOrders = orders.filter((order) => {
    if (!order) return false;

    if (filterDate === 'today' && !isTodayOrder(order.ordered_at)) {
      return false;
    }

    const matchesSearch =
      (order.identifier || '').toLowerCase().includes((searchQuery || '').toLowerCase()) ||
      (order.kot_number || '').toLowerCase().includes((searchQuery || '').toLowerCase());

    const matchesStatus = filterStatus === 'all' || (order.status || '') === filterStatus;
    const matchesType = filterType === 'all' || (order.order_type || '') === filterType;

    return matchesSearch && matchesStatus && matchesType;
  });

  const tabFilteredOrders = filteredOrders.filter((order) => {
    if (!order) return false;
    const status = order.status || 'pending';
    if (selectedTab === 'all') return true;
    if (selectedTab === 'pending') return status === 'pending';
    if (selectedTab === 'preparing') return status === 'preparing';
    if (selectedTab === 'ready') return status === 'ready';
    if (selectedTab === 'completed')
      return status === 'completed' || status === 'delivered' || status === 'served';
    return true;
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = tabFilteredOrders.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(tabFilteredOrders.length / itemsPerPage);

  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);
  const handleNextPage = () => currentPage < totalPages && setCurrentPage(currentPage + 1);
  const handlePreviousPage = () => currentPage > 1 && setCurrentPage(currentPage - 1);

  const getVisiblePages = () => {
    const pages = [];
    const pageGroupSize = 10;
    const currentGroup = Math.ceil(currentPage / pageGroupSize);
    const startPage = (currentGroup - 1) * pageGroupSize + 1;
    const endPage = Math.min(startPage + pageGroupSize - 1, totalPages);
    for (let i = startPage; i <= endPage; i++) pages.push(i);
    return pages;
  };

  const formatTime = (dateTimeString) => {
    if (!dateTimeString) return 'N/A';
    try {
      const parts = dateTimeString.split('T');
      if (parts.length < 2) return 'N/A';
      const timePart = parts[1].split('.')[0];
      const timeParts = timePart.split(':');
      if (timeParts.length < 2) return 'N/A';
      return `${timeParts[0]}:${timeParts[1]}`;
    } catch (error) {
      return 'N/A';
    }
  };

  const tabs = [
    {
      id: 'all',
      label: 'All Orders',
      icon: FaClipboardList,
      count: filteredOrders.length,
    },
    {
      id: 'pending',
      label: 'Pending',
      icon: FaClock,
      count: filteredOrders.filter((o) => o && o.status === 'pending').length,
    },
    {
      id: 'preparing',
      label: 'Preparing',
      icon: FaHourglassHalf,
      count: filteredOrders.filter((o) => o && o.status === 'preparing').length,
    },
    {
      id: 'ready',
      label: 'Ready',
      icon: FaCheckCircle,
      count: filteredOrders.filter((o) => o && o.status === 'ready').length,
    },
    {
      id: 'completed',
      label: 'Completed',
      icon: FaCheckCircle,
      count: filteredOrders.filter(
        (o) => o && (o.status === 'completed' || o.status === 'delivered' || o.status === 'served')
      ).length,
    },
  ];

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <header className="flex justify-between items-center mb-3 bg-white p-3">
        <h1 className="text-black text-xl font-bold ml-2">Food & Beverages Management</h1>
        <div className="flex space-x-6">
          <button
            onClick={() => navigate('/dashboard/core-modules/f&b-management/track-order-frontdesk')}
            className="btn"
          >
            Order Overview
          </button>
          <button
            onClick={() => navigate('/dashboard/core-modules/f&b-management/order-food')}
            className="btn"
          >
            Order Food
          </button>
          <button
            onClick={() => navigate('/dashboard/core-modules/f&b-management/dining-tables')}
            className="btn"
          >
            Dining Tables
          </button>
          <button
            onClick={() => navigate('/dashboard/core-modules/f&b-management/track-order-kitchen')}
            className="btn font-bold text-black"
          >
            Kitchen Display System(KDS)
          </button>
          <button
            onClick={() => navigate('/dashboard/core-modules/f&b-management/order-invoicing')}
            className="btn text-gray-600 hover:text-black"
          >
            Billing & Invoicing
          </button>
        </div>
      </header>

      <div className="text-center mb-6 mt-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-1">Kitchen Display System</h1>
        <p className="text-sm font-poppins font-semibold text-center mb-1 text-gray-600">
          Manage incoming orders, update preparation status, and coordinate kitchen operations in
          real time.
        </p>
        <hr className="border border-gray-400 mt-1 w-64 mx-auto mb-7" />
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Orders</p>
              <p className="text-2xl font-semibold text-gray-800">{filteredOrders.length}</p>
            </div>
            <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
              <FaClipboardList className="text-gray-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Pending</p>
              <p className="text-2xl font-semibold text-amber-600">
                {filteredOrders.filter((o) => o && o.status === 'pending').length}
              </p>
            </div>
            <div className="w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center">
              <FaClock className="text-amber-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">In Kitchen</p>
              <p className="text-2xl font-semibold text-indigo-600">
                {filteredOrders.filter((o) => o && o.status === 'preparing').length}
              </p>
            </div>
            <div className="w-10 h-10 rounded-full bg-indigo-50 flex items-center justify-center">
              <FaHourglassHalf className="text-indigo-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Ready to Serve</p>
              <p className="text-2xl font-semibold text-emerald-600">
                {filteredOrders.filter((o) => o && o.status === 'ready').length}
              </p>
            </div>
            <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center">
              <FaCheckCircle className="text-emerald-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-4">
        <div className="flex flex-wrap space-x-1">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = selectedTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => {
                  setSelectedTab(tab.id);
                  setCurrentPage(1);
                }}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? 'border-b-2 border-gray-800 text-gray-800'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Icon size={14} />
                <span>{tab.label}</span>
                <span
                  className={`text-xs px-1.5 py-0.5 rounded-full ${
                    isActive ? 'bg-gray-100 text-gray-700' : 'bg-gray-100 text-gray-500'
                  }`}
                >
                  {tab.count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="relative w-80">
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
          <input
            type="text"
            placeholder="Search by room, table, or parcel number..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
            className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-gray-300"
          />
        </div>
        <div className="flex gap-2">
          <select
            value={filterType}
            onChange={(e) => {
              setFilterType(e.target.value);
              setCurrentPage(1);
            }}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white text-gray-700"
          >
            <option value="all">All Types</option>
            <option value="room">Room Service</option>
            <option value="dining">Dine In</option>
            <option value="parcel">Parcel</option>
          </select>
          <select
            value={filterStatus}
            onChange={(e) => {
              setFilterStatus(e.target.value);
              setCurrentPage(1);
            }}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white text-gray-700"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="accepted">Accepted</option>
            <option value="preparing">Preparing</option>
            <option value="ready">Ready</option>
            <option value="served">Served</option>
            <option value="completed">Completed</option>
          </select>
          <select
            value={filterDate}
            onChange={(e) => {
              setFilterDate(e.target.value);
              setCurrentPage(1);
            }}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white text-gray-700"
          >
            <option value="today">Today's Orders</option>
            <option value="all">All Orders</option>
          </select>
        </div>
      </div>

      {/* Orders Grid */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <FaSpinner className="animate-spin text-gray-400 text-2xl" />
        </div>
      ) : currentItems.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center shadow-sm">
          <div className="max-w-md mx-auto">
            <div className="w-24 h-24 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <FaClipboardList className="text-gray-300 text-4xl" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-1">All Orders Complete! ✨</h3>
            <p className="text-gray-800 text-lg">
              Kitchen is clean and ready for action, New orders will appear here automatically
            </p>
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {currentItems.map((order) => {
              if (!order) return null;
              const time = formatTime(order.ordered_at);
              const statusConfig = getStatusConfig(order.status);
              const StatusIcon = statusConfig.icon;
              const TypeIcon = getTypeIcon(order.order_type);

              return (
                <div
                  key={order.id}
                  onClick={() => setSelectedOrder(order)}
                  className={`bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer overflow-hidden ${statusConfig.border} border-l-4`}
                >
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                          <TypeIcon className="text-gray-600 text-sm" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                              {getTypeLabel(order.order_type)}
                            </span>
                            <span className="text-xs text-gray-400">•</span>
                            <span className="text-xs font-mono text-gray-500">
                              #{order.kot_number || order.id?.slice(0, 8) || 'N/A'}
                            </span>
                          </div>
                          <div className="font-bold text-lg text-gray-800 mt-0.5">
                            {order.identifier}
                          </div>
                        </div>
                      </div>
                      <div
                        className={`flex items-center gap-1.5 px-2 py-1 rounded-full ${statusConfig.bg}`}
                      >
                        <StatusIcon className={`${statusConfig.color} text-xs`} />
                        <span className={`text-xs font-medium ${statusConfig.color}`}>
                          {statusConfig.label}
                        </span>
                      </div>
                    </div>

                    <div className="mb-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">Time:</span>
                        <span className="text-gray-600">{time}</span>
                      </div>
                    </div>

                    {/* Food Items */}
                    <div className="mt-3">
                      <div className="text-sm font-medium text-gray-700 mb-2">Items:</div>
                      <div className="grid grid-cols-2 gap-3 max-h-40 overflow-y-auto pr-1">
                        {order.items &&
                          order.items.map((item, idx) => {
                            const itemStatusConfig = getItemStatusConfig(item.item_status);
                            return (
                              <div key={idx} className="text-sm text-gray-600">
                                <div className="flex items-center justify-between gap-2">
                                  <div className="flex items-center gap-1 flex-1 min-w-0">
                                    <span className="truncate">{item.name}</span>
                                    <span className="font-medium text-gray-700 flex-shrink-0">
                                      ×{item.quantity}
                                    </span>
                                  </div>
                                  {order.order_type !== 'room' && item.item_status && (
                                    <span
                                      className={`text-xs px-1.5 py-0.5 rounded-full flex-shrink-0 ${itemStatusConfig.bg} ${itemStatusConfig.color}`}
                                    >
                                      {itemStatusConfig.label}
                                    </span>
                                  )}
                                </div>
                                {item.special_request && (
                                  <div className="mt-1 text-xs text-amber-600 bg-amber-50 inline-block px-2 py-0.5 rounded truncate max-w-full">
                                    <span className="font-medium">Note:</span>{' '}
                                    {item.special_request}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                      </div>
                      {(order.special_request || order.kitchen_notes) && (
                        <div className="mt-3 pt-2 border-t border-gray-100">
                          <div className="text-xs font-medium text-gray-500 mb-1">
                            Kitchen Instructions:
                          </div>
                          <div className="text-sm text-amber-700 bg-amber-50 p-2 rounded-md">
                            {order.kitchen_notes || order.special_request}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-between items-center mt-6 px-2">
              <div className="text-sm text-gray-500">
                Showing {indexOfFirstItem + 1}–{Math.min(indexOfLastItem, tabFilteredOrders.length)}{' '}
                of {tabFilteredOrders.length} orders
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={handlePreviousPage}
                  disabled={currentPage === 1}
                  className="p-2 rounded-md border border-gray-200 bg-white text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  <FaArrowLeft size={12} />
                </button>

                {currentPage > 10 && (
                  <>
                    <button
                      onClick={() => handlePageChange(1)}
                      className="px-3 py-1 text-sm rounded-md text-gray-600 hover:bg-gray-100"
                    >
                      1
                    </button>
                    <span className="px-1 text-gray-400">...</span>
                  </>
                )}

                {getVisiblePages().map((page) => (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`px-3 py-1 text-sm rounded-md ${
                      currentPage === page
                        ? 'bg-gray-800 text-white'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    {page}
                  </button>
                ))}

                {currentPage < totalPages - 9 && (
                  <>
                    <span className="px-1 text-gray-400">...</span>
                    <button
                      onClick={() => handlePageChange(totalPages)}
                      className="px-3 py-1 text-sm rounded-md text-gray-600 hover:bg-gray-100"
                    >
                      {totalPages}
                    </button>
                  </>
                )}

                <button
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                  className="p-2 rounded-md border border-gray-200 bg-white text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  <FaArrowRight size={12} />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Order Details Modal */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto relative shadow-xl">
            <button
              onClick={() => {
                setSelectedOrder(null);
              }}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 z-10"
            >
              <FaTimes className="w-5 h-5" />
            </button>

            <div className="p-6 border-b border-gray-100 sticky top-0 bg-white">
              <div className="flex justify-between items-center mb-2">
                <h2 className="text-2xl font-bold text-gray-800">{selectedOrder.identifier}</h2>
                <div
                  className={`flex items-center gap-1.5 px-2 py-1 mt-4 rounded-full ${getStatusConfig(selectedOrder.status).bg}`}
                >
                  {React.createElement(getStatusConfig(selectedOrder.status).icon, {
                    className: `${getStatusConfig(selectedOrder.status).color} text-xs`,
                  })}
                  <span
                    className={`text-xs font-medium ${getStatusConfig(selectedOrder.status).color}`}
                  >
                    {getStatusConfig(selectedOrder.status).label}
                  </span>
                </div>
              </div>
              <p className="text-sm text-gray-500">KOT: {selectedOrder.kot_number}</p>
            </div>

            <div className="p-6">
              <h3 className="font-semibold text-gray-700 mb-4">Order Items</h3>

              {(selectedOrder.special_request || selectedOrder.kitchen_notes) && (
                <div className="mb-4 p-3 bg-amber-50 rounded-lg border border-amber-100">
                  <div className="flex items-center gap-2 text-amber-700 font-medium text-sm mb-1">
                    <FaClipboardList size={12} />
                    <span>Kitchen Instructions</span>
                  </div>
                  <p className="text-amber-800 text-sm">
                    {selectedOrder.kitchen_notes || selectedOrder.special_request}
                  </p>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
                {selectedOrder.items &&
                  selectedOrder.items.map((item, index) => {
                    const itemStatusConfig = getItemStatusConfig(item.item_status);
                    const isTerminal =
                      item.item_status === 'served' || item.item_status === 'cancelled';
                    const canUpdate = selectedOrder.order_type !== 'room';
                    const canForward = !isTerminal;

                    return (
                      <div
                        key={index}
                        className="border border-gray-100 rounded-lg p-2 bg-gray-50/30"
                      >
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-semibold text-gray-800 text-sm">
                                {item.name}
                              </span>
                              <span className="font-medium text-gray-600 bg-white px-2 py-0.5 rounded-full text-sm">
                                ×{item.quantity}
                              </span>
                            </div>
                            {item.special_request && (
                              <p className="text-sm text-amber-600 mt-1 bg-amber-50 inline-block px-2 py-1 rounded">
                                {item.special_request}
                              </p>
                            )}
                          </div>
                          <div
                            className={`px-2 py-1 rounded-full ${itemStatusConfig.bg} ${itemStatusConfig.color} text-xs font-medium`}
                          >
                            {itemStatusConfig.label}
                          </div>
                        </div>

                        {canUpdate && (
                          <>
                            {canForward && (
                              <div className="flex gap-2 mt-3 pt-3 border-t border-gray-200 flex-wrap">
                                {item.item_status === 'pending' && (
                                  <button
                                    onClick={() => {
                                      updateItemStatus(
                                        selectedOrder.id,
                                        item.id,
                                        'accepted',
                                        selectedOrder.order_type
                                      );
                                      setSelectedOrder({
                                        ...selectedOrder,
                                        items: selectedOrder.items.map((i) =>
                                          i.id === item.id ? { ...i, item_status: 'accepted' } : i
                                        ),
                                      });
                                    }}
                                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm font-medium flex items-center gap-2"
                                  >
                                    <FaCheck size={12} />
                                    Accept Order
                                  </button>
                                )}

                                {item.item_status === 'accepted' && (
                                  <button
                                    onClick={() => {
                                      updateItemStatus(
                                        selectedOrder.id,
                                        item.id,
                                        'preparing',
                                        selectedOrder.order_type
                                      );
                                      setSelectedOrder({
                                        ...selectedOrder,
                                        items: selectedOrder.items.map((i) =>
                                          i.id === item.id ? { ...i, item_status: 'preparing' } : i
                                        ),
                                      });
                                    }}
                                    className="px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors text-sm font-medium flex items-center gap-2"
                                  >
                                    <FaFire size={12} />
                                    Start Preparing
                                  </button>
                                )}

                                {item.item_status === 'preparing' && (
                                  <button
                                    onClick={() => {
                                      updateItemStatus(
                                        selectedOrder.id,
                                        item.id,
                                        'ready',
                                        selectedOrder.order_type
                                      );
                                      setSelectedOrder({
                                        ...selectedOrder,
                                        items: selectedOrder.items.map((i) =>
                                          i.id === item.id ? { ...i, item_status: 'ready' } : i
                                        ),
                                      });
                                    }}
                                    className="px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors text-sm font-medium flex items-center gap-2"
                                  >
                                    <FaCheck size={12} />
                                    Mark Ready
                                  </button>
                                )}

                                {item.item_status === 'ready' && (
                                  <button
                                    onClick={() => {
                                      updateItemStatus(
                                        selectedOrder.id,
                                        item.id,
                                        'served',
                                        selectedOrder.order_type
                                      );
                                      setSelectedOrder({
                                        ...selectedOrder,
                                        items: selectedOrder.items.map((i) =>
                                          i.id === item.id ? { ...i, item_status: 'served' } : i
                                        ),
                                      });
                                    }}
                                    className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm font-medium flex items-center gap-2"
                                  >
                                    <FaPlay size={12} />
                                    Mark Served
                                  </button>
                                )}

                                {(item.item_status === 'pending' ||
                                  item.item_status === 'accepted' ||
                                  item.item_status === 'preparing') && (
                                  <button
                                    onClick={() => {
                                      updateItemStatus(
                                        selectedOrder.id,
                                        item.id,
                                        'cancelled',
                                        selectedOrder.order_type
                                      );
                                      setSelectedOrder({
                                        ...selectedOrder,
                                        items: selectedOrder.items.map((i) =>
                                          i.id === item.id ? { ...i, item_status: 'cancelled' } : i
                                        ),
                                      });
                                    }}
                                    className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm font-medium flex items-center gap-2"
                                  >
                                    <FaBan size={12} />
                                    Cancel Item
                                  </button>
                                )}
                              </div>
                            )}

                            {/* Revert / Change item status (available even for terminal states) */}
                            {getItemRevertOptions(item.item_status).length > 0 && (
                              <div className="mt-3 pt-3 border-t border-gray-200">
                                <label className="block text-xs font-medium text-gray-500 mb-1">
                                  Change item status back to
                                </label>
                                <select
                                  defaultValue=""
                                  onChange={(e) => {
                                    const newStatus = e.target.value;
                                    if (!newStatus) return;
                                    const confirm = window.confirm(
                                      `Change "${item.name}" from "${item.item_status}" back to "${newStatus}"?`
                                    );
                                    if (!confirm) {
                                      e.target.value = '';
                                      return;
                                    }
                                    updateItemStatus(
                                      selectedOrder.id,
                                      item.id,
                                      newStatus,
                                      selectedOrder.order_type
                                    );
                                    setSelectedOrder({
                                      ...selectedOrder,
                                      items: selectedOrder.items.map((i) =>
                                        i.id === item.id ? { ...i, item_status: newStatus } : i
                                      ),
                                    });
                                    e.target.value = '';
                                  }}
                                  className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-xs bg-white text-gray-700"
                                >
                                  <option value="">Select previous status…</option>
                                  {getItemRevertOptions(item.item_status).map((s) => (
                                    <option key={s} value={s}>
                                      {getItemStatusConfig(s).label}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>

            {/* Room Order Buttons */}
            {selectedOrder.order_type === 'room' && (
              <div className="p-6 border-t border-gray-100">
                {/* Forward action buttons */}
                <div className="flex gap-3 flex-wrap">
                  {selectedOrder.status === 'pending' && (
                    <button
                      onClick={() => {
                        updateOrderStatus(selectedOrder.id, 'accepted', selectedOrder.order_type);
                        setSelectedOrder({ ...selectedOrder, status: 'accepted' });
                      }}
                      className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm font-medium"
                    >
                      Accept Order
                    </button>
                  )}

                  {selectedOrder.status === 'accepted' && (
                    <button
                      onClick={() => {
                        updateOrderStatus(selectedOrder.id, 'preparing', selectedOrder.order_type);
                        setSelectedOrder({ ...selectedOrder, status: 'preparing' });
                      }}
                      className="flex-1 px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors text-sm font-medium"
                    >
                      Start Preparing
                    </button>
                  )}

                  {selectedOrder.status === 'preparing' && (
                    <button
                      onClick={() => {
                        updateOrderStatus(selectedOrder.id, 'ready', selectedOrder.order_type);
                        setSelectedOrder({ ...selectedOrder, status: 'ready' });
                      }}
                      className="flex-1 px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors text-sm font-medium"
                    >
                      Mark Ready
                    </button>
                  )}

                  {selectedOrder.status === 'ready' && (
                    <button
                      onClick={() => {
                        updateOrderStatus(selectedOrder.id, 'delivered', selectedOrder.order_type);
                        setSelectedOrder({ ...selectedOrder, status: 'delivered' });
                        setTimeout(() => setSelectedOrder(null), 1000);
                      }}
                      className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm font-medium"
                    >
                      Mark Delivered
                    </button>
                  )}

                  {selectedOrder.status !== 'cancelled' && selectedOrder.status !== 'delivered' && (
                    <button
                      onClick={() => {
                        updateOrderStatus(selectedOrder.id, 'cancelled', selectedOrder.order_type);
                        setSelectedOrder({ ...selectedOrder, status: 'cancelled' });
                        setTimeout(() => setSelectedOrder(null), 1000);
                      }}
                      className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm font-medium"
                    >
                      Cancel Order
                    </button>
                  )}
                </div>

                {/* Revert / Change Status controls */}
                {getRevertOptions(selectedOrder.status, selectedOrder.order_type).length > 0 && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <label className="block text-xs font-medium text-gray-500 mb-2">
                      Change status back to (undo)
                    </label>
                    <div className="flex items-center gap-2">
                      <select
                        defaultValue=""
                        onChange={(e) => {
                          const newStatus = e.target.value;
                          if (!newStatus) return;
                          const confirm = window.confirm(
                            `Change order status from "${selectedOrder.status}" back to "${newStatus}"?`
                          );
                          if (!confirm) {
                            e.target.value = '';
                            return;
                          }
                          updateOrderStatus(selectedOrder.id, newStatus, selectedOrder.order_type);
                          setSelectedOrder({ ...selectedOrder, status: newStatus });
                          e.target.value = '';
                        }}
                        className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white text-gray-700"
                      >
                        <option value="">Select previous status…</option>
                        {getRevertOptions(selectedOrder.status, selectedOrder.order_type).map(
                          (s) => (
                            <option key={s} value={s}>
                              {getStatusConfig(s).label}
                            </option>
                          )
                        )}
                      </select>
                      <button
                        onClick={() => {
                          if (
                            window.confirm(
                              'Reset this order to "Pending"? This will require re-accepting and re-preparing.'
                            )
                          ) {
                            updateOrderStatus(
                              selectedOrder.id,
                              'pending',
                              selectedOrder.order_type
                            );
                            setSelectedOrder({ ...selectedOrder, status: 'pending' });
                          }
                        }}
                        className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium whitespace-nowrap"
                      >
                        Reset to Pending
                      </button>
                    </div>
                    <p className="text-xs text-gray-400 mt-2">
                      Reverting also updates the kitchen order on the server. Use this if a status
                      was set by mistake.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <ToastContainer position="top-right" />
    </div>
  );
};

export default TrackOrderKitchen;

// import React, { useState, useEffect } from 'react';
// import {
//   FaTimes,
//   FaClock,
//   FaSearch,
//   FaSpinner,
//   FaArrowLeft,
//   FaArrowRight,
//   FaClipboardList,
//   FaConciergeBell,
//   FaBox,
//   FaUtensils,
//   FaCheckCircle,
//   FaHourglassHalf,
//   FaCheck,
//   FaBan,
//   FaFire,
//   FaPlay,
// } from 'react-icons/fa';
// import { toast, ToastContainer } from 'react-toastify';
// import 'react-toastify/dist/ReactToastify.css';
// import { useNavigate } from 'react-router-dom';

// const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

// const TrackOrderKitchen = () => {
//   const [orders, setOrders] = useState([]);
//   const [searchQuery, setSearchQuery] = useState('');
//   const [filterStatus, setFilterStatus] = useState('all');
//   const [filterType, setFilterType] = useState('all');
//   const [filterDate, setFilterDate] = useState('today');
//   const [selectedOrder, setSelectedOrder] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [currentPage, setCurrentPage] = useState(1);
//   const [itemsPerPage] = useState(20);
//   const [selectedTab, setSelectedTab] = useState('all');
//   const navigate = useNavigate();

//   useEffect(() => {
//     fetchAllOrders();
//   }, []);

//   const fetchAllOrders = async () => {
//     setLoading(true);
//     try {
//       const roomRes = await fetch(`${BQ_BASE_URL}/bq/api/kitchen/orders`, {
//         headers: { accept: 'application/json' },
//       });

//       const restaurantRes = await fetch(`${BQ_BASE_URL}/bq/api/restaurant/kitchen/orders`, {
//         headers: { accept: 'application/json' },
//       });

//       let roomOrders = [];
//       let restaurantOrders = [];

//       if (roomRes.ok) {
//         const roomData = await roomRes.json();
//         roomOrders = roomData || [];
//       }

//       if (restaurantRes.ok) {
//         const restaurantData = await restaurantRes.json();
//         restaurantOrders = restaurantData.orders || [];
//       }

//       const transformedRoomOrders = roomOrders.map((order) => ({
//         id: order.order_id,
//         kot_number: order.order_id?.slice(0, 8) || 'N/A',
//         order_type: 'room',
//         identifier: `Room ${order.room_number || '—'}`,
//         room_number: order.room_number,
//         status: order.status ? order.status.toLowerCase() : 'pending',
//         original_status: order.status,
//         ordered_at: order.ordered_at,
//         items: (order.ordered_items || []).map((item, idx) => ({
//           id: `${order.order_id}_item_${idx}`,
//           name: item.name || 'Unknown',
//           quantity: item.quantity || 1,
//           item_status: order.status ? order.status.toLowerCase() : 'pending',
//         })),
//       }));
//       const transformedRestaurantOrders = restaurantOrders.map((order) => {
//         let identifier = '';
//         if (order.order_type === 'dining') {
//           identifier = `Table ${order.table_number || '—'}`;
//         } else {
//           identifier = `Parcel ${order.parcel_number || '—'}`;
//         }

//         const orderStatus = order.kot_status || order.order_status;

//         return {
//           id: order.order_id,
//           kot_number: order.kot_number || order.order_id?.slice(0, 8) || 'N/A',
//           order_type: order.order_type || 'parcel',
//           identifier: identifier,
//           status: orderStatus ? orderStatus.toLowerCase() : 'pending',
//           original_status: orderStatus,
//           ordered_at: order.ordered_at,
//           special_request: order.special_request, // Add order-level special request
//           kitchen_notes: order.kitchen_notes, // Add kitchen notes
//           items: (order.items || []).map((item) => ({
//             id: item.item_id,
//             name: item.name || 'Unknown',
//             quantity: item.quantity || 1,
//             item_status: (item.item_status || orderStatus || 'pending').toLowerCase(),
//             special_request: item.special_request, // Keep individual item special request
//           })),
//         };
//       });

//       const allOrders = [...transformedRoomOrders, ...transformedRestaurantOrders];
//       setOrders(allOrders);
//     } catch (error) {
//       console.error('Error fetching orders:', error);
//       toast.error('Failed to fetch kitchen orders');
//     } finally {
//       setLoading(false);
//     }
//   };

//   const updateOrderStatus = async (id, status, orderType) => {
//     try {
//       let url;
//       if (orderType === 'room') {
//         url = `${BQ_BASE_URL}/bq/api/kitchen/orders/${id}/status`;
//         // status is already lowercase: "accepted", "preparing", "ready", "delivered", "cancelled"
//         const response = await fetch(url, {
//           method: 'PATCH',
//           headers: {
//             accept: 'application/json',
//             'Content-Type': 'application/json',
//           },
//           body: JSON.stringify({ status: status }),
//         });

//         if (response.ok) {
//           setOrders((prevOrders) =>
//             prevOrders.map((order) =>
//               order.id === id ? { ...order, status: status.toLowerCase() } : order
//             )
//           );
//           toast.success(`Order ${status}!`);
//         } else {
//           const error = await response.json();
//           toast.error(`Failed to update status: ${error.detail || 'Unknown error'}`);
//         }
//       } else {
//         // restaurant orders logic...
//         url = `${BQ_BASE_URL}/bq/api/restaurant/orders/${id}/status`;
//         const response = await fetch(url, {
//           method: 'PATCH',
//           headers: {
//             accept: 'application/json',
//             'Content-Type': 'application/json',
//           },
//           body: JSON.stringify({ order_status: status.toLowerCase() }),
//         });

//         if (response.ok) {
//           setOrders((prevOrders) =>
//             prevOrders.map((order) =>
//               order.id === id ? { ...order, status: status.toLowerCase() } : order
//             )
//           );
//           toast.success(`Order ${status}!`);
//         } else {
//           const error = await response.json();
//           toast.error(`Failed to update status: ${error.detail || 'Unknown error'}`);
//         }
//       }
//     } catch (error) {
//       console.error('Error updating order status:', error);
//       toast.error('An error occurred while updating the status.');
//     }
//   };
//   const updateItemStatus = async (orderId, itemId, newStatus, orderType) => {
//     if (orderType === 'room') {
//       toast.error('Individual item status update not available for room orders');
//       return;
//     }

//     try {
//       // First update the item status
//       const url = `${BQ_BASE_URL}/bq/api/restaurant/orders/${orderId}/items/${itemId}/status`;

//       const response = await fetch(url, {
//         method: 'PATCH',
//         headers: {
//           accept: 'application/json',
//           'Content-Type': 'application/json',
//         },
//         body: JSON.stringify({ item_status: newStatus.toLowerCase() }),
//       });

//       if (response.ok) {
//         // Update local state for items
//         setOrders((prevOrders) =>
//           prevOrders.map((order) => {
//             if (order.id === orderId) {
//               const updatedItems = order.items.map((item) =>
//                 item.id === itemId ? { ...item, item_status: newStatus.toLowerCase() } : item
//               );

//               const allItemStatuses = updatedItems.map((i) => i.item_status);
//               let newOrderStatus = order.status;

//               if (allItemStatuses.every((s) => s === 'served' || s === 'cancelled')) {
//                 newOrderStatus = 'served';
//               } else if (
//                 allItemStatuses.every((s) => s === 'ready' || s === 'served' || s === 'cancelled')
//               ) {
//                 newOrderStatus = 'ready';
//               } else if (allItemStatuses.some((s) => s === 'preparing')) {
//                 newOrderStatus = 'preparing';
//               } else if (allItemStatuses.some((s) => s === 'accepted')) {
//                 newOrderStatus = 'accepted';
//               }

//               // Update the order status on the backend
//               if (newOrderStatus !== order.status) {
//                 // Call the order status update API
//                 fetch(`${BQ_BASE_URL}/bq/api/restaurant/orders/${orderId}/status`, {
//                   method: 'PATCH',
//                   headers: {
//                     accept: 'application/json',
//                     'Content-Type': 'application/json',
//                   },
//                   body: JSON.stringify({ order_status: newOrderStatus }),
//                 }).catch((err) => console.error('Error updating order status:', err));
//               }

//               return { ...order, items: updatedItems, status: newOrderStatus };
//             }
//             return order;
//           })
//         );

//         toast.success(`Item marked as ${newStatus}!`);
//       } else {
//         const error = await response.json();
//         toast.error(`Failed to update item: ${error.detail || 'Unknown error'}`);
//       }
//     } catch (error) {
//       console.error('Error updating item status:', error);
//       toast.error('An error occurred while updating the item status.');
//     }
//   };
//   const isTodayOrder = (orderDate) => {
//     if (!orderDate) return false;
//     try {
//       // Compare date strings directly (YYYY-MM-DD)
//       const todayStr = new Date().toISOString().split('T')[0];
//       const orderDateStr = orderDate.split('T')[0];
//       return orderDateStr === todayStr;
//     } catch (error) {
//       return false;
//     }
//   };
//   const getStatusConfig = (status) => {
//     const safeStatus = status ? status.toLowerCase() : 'pending';
//     const statusMap = {
//       pending: {
//         label: 'Pending',
//         icon: FaClock,
//         color: 'text-amber-600',
//         bg: 'bg-amber-50',
//         border: 'border-l-amber-500',
//       },
//       accepted: {
//         label: 'Accepted',
//         icon: FaCheckCircle,
//         color: 'text-blue-600',
//         bg: 'bg-blue-50',
//         border: 'border-l-blue-500',
//       },
//       preparing: {
//         label: 'Preparing',
//         icon: FaHourglassHalf,
//         color: 'text-indigo-600',
//         bg: 'bg-indigo-50',
//         border: 'border-l-indigo-500',
//       },
//       ready: {
//         label: 'Ready',
//         icon: FaCheckCircle,
//         color: 'text-emerald-600',
//         bg: 'bg-emerald-50',
//         border: 'border-l-emerald-500',
//       },
//       completed: {
//         label: 'Completed',
//         icon: FaCheckCircle,
//         color: 'text-gray-600',
//         bg: 'bg-gray-50',
//         border: 'border-l-gray-400',
//       },
//       delivered: {
//         label: 'Delivered',
//         icon: FaCheckCircle,
//         color: 'text-gray-600',
//         bg: 'bg-gray-50',
//         border: 'border-l-gray-400',
//       },
//       served: {
//         label: 'Served',
//         icon: FaCheckCircle,
//         color: 'text-green-600',
//         bg: 'bg-green-50',
//         border: 'border-l-green-500',
//       },
//       cancelled: {
//         label: 'Cancelled',
//         icon: FaTimes,
//         color: 'text-red-600',
//         bg: 'bg-red-50',
//         border: 'border-l-red-500',
//       },
//     };
//     return statusMap[safeStatus] || statusMap.pending;
//   };

//   const getItemStatusConfig = (status) => {
//     const safeStatus = status ? status.toLowerCase() : 'pending';
//     const statusMap = {
//       pending: {
//         label: 'Pending',
//         color: 'text-amber-600',
//         bg: 'bg-amber-50',
//         nextStatus: 'accepted',
//         nextLabel: 'Accept Order',
//       },
//       accepted: {
//         label: 'Accepted',
//         color: 'text-blue-600',
//         bg: 'bg-blue-50',
//         nextStatus: 'preparing',
//         nextLabel: 'Start Preparing',
//       },
//       preparing: {
//         label: 'Preparing',
//         color: 'text-indigo-600',
//         bg: 'bg-indigo-50',
//         nextStatus: 'ready',
//         nextLabel: 'Mark Ready',
//       },
//       ready: {
//         label: 'Ready',
//         color: 'text-emerald-600',
//         bg: 'bg-emerald-50',
//         nextStatus: 'served',
//         nextLabel: 'Mark Served',
//       },
//       served: {
//         label: 'Served',
//         color: 'text-green-600',
//         bg: 'bg-green-50',
//         nextStatus: null,
//         nextLabel: null,
//       },
//       cancelled: {
//         label: 'Cancelled',
//         color: 'text-red-600',
//         bg: 'bg-red-50',
//         nextStatus: null,
//         nextLabel: null,
//       },
//     };
//     return statusMap[safeStatus] || statusMap.pending;
//   };

//   const getTypeIcon = (type) => {
//     const icons = {
//       room: FaConciergeBell,
//       dining: FaUtensils,
//       parcel: FaBox,
//     };
//     return icons[type] || FaConciergeBell;
//   };

//   const getTypeLabel = (type) => {
//     const labels = {
//       room: 'Room',
//       dining: 'Dine In',
//       parcel: 'Parcel',
//     };
//     return labels[type] || type;
//   };

//   const filteredOrders = orders.filter((order) => {
//     if (!order) return false;

//     if (filterDate === 'today' && !isTodayOrder(order.ordered_at)) {
//       return false;
//     }

//     const matchesSearch =
//       (order.identifier || '').toLowerCase().includes((searchQuery || '').toLowerCase()) ||
//       (order.kot_number || '').toLowerCase().includes((searchQuery || '').toLowerCase());

//     const matchesStatus = filterStatus === 'all' || (order.status || '') === filterStatus;
//     const matchesType = filterType === 'all' || (order.order_type || '') === filterType;

//     return matchesSearch && matchesStatus && matchesType;
//   });

//   const tabFilteredOrders = filteredOrders.filter((order) => {
//     if (!order) return false;
//     const status = order.status || 'pending';
//     if (selectedTab === 'all') return true;
//     if (selectedTab === 'pending') return status === 'pending';
//     if (selectedTab === 'preparing') return status === 'preparing';
//     if (selectedTab === 'ready') return status === 'ready';
//     if (selectedTab === 'completed')
//       return status === 'completed' || status === 'delivered' || status === 'served';
//     return true;
//   });

//   const indexOfLastItem = currentPage * itemsPerPage;
//   const indexOfFirstItem = indexOfLastItem - itemsPerPage;
//   const currentItems = tabFilteredOrders.slice(indexOfFirstItem, indexOfLastItem);
//   const totalPages = Math.ceil(tabFilteredOrders.length / itemsPerPage);

//   const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);
//   const handleNextPage = () => currentPage < totalPages && setCurrentPage(currentPage + 1);
//   const handlePreviousPage = () => currentPage > 1 && setCurrentPage(currentPage - 1);

//   const getVisiblePages = () => {
//     const pages = [];
//     const pageGroupSize = 10;
//     const currentGroup = Math.ceil(currentPage / pageGroupSize);
//     const startPage = (currentGroup - 1) * pageGroupSize + 1;
//     const endPage = Math.min(startPage + pageGroupSize - 1, totalPages);
//     for (let i = startPage; i <= endPage; i++) pages.push(i);
//     return pages;
//   };

//   const formatTime = (dateTimeString) => {
//     if (!dateTimeString) return 'N/A';
//     try {
//       // Extract time directly from the string without any conversion
//       const parts = dateTimeString.split('T');
//       if (parts.length < 2) return 'N/A';
//       const timePart = parts[1].split('.')[0]; // Get HH:MM:SS
//       const timeParts = timePart.split(':');
//       if (timeParts.length < 2) return 'N/A';
//       return `${timeParts[0]}:${timeParts[1]}`; // Return HH:MM as is
//     } catch (error) {
//       return 'N/A';
//     }
//   };

//   const tabs = [
//     {
//       id: 'all',
//       label: 'All Orders',
//       icon: FaClipboardList,
//       count: filteredOrders.length,
//     },
//     {
//       id: 'pending',
//       label: 'Pending',
//       icon: FaClock,
//       count: filteredOrders.filter((o) => o && o.status === 'pending').length,
//     },
//     {
//       id: 'preparing',
//       label: 'Preparing',
//       icon: FaHourglassHalf,
//       count: filteredOrders.filter((o) => o && o.status === 'preparing').length,
//     },
//     {
//       id: 'ready',
//       label: 'Ready',
//       icon: FaCheckCircle,
//       count: filteredOrders.filter((o) => o && o.status === 'ready').length,
//     },
//     {
//       id: 'completed',
//       label: 'Completed',
//       icon: FaCheckCircle,
//       count: filteredOrders.filter(
//         (o) => o && (o.status === 'completed' || o.status === 'delivered' || o.status === 'served')
//       ).length,
//     },
//   ];

//   return (
//     <div className="p-6 bg-gray-50 min-h-screen">
//       <header className="flex justify-between items-center mb-3 bg-white p-3">
//         <h1 className="text-black text-xl font-bold ml-2">Food & Beverages Management</h1>
//         <div className="flex space-x-6">
//           <button
//             onClick={() => navigate('/dashboard/core-modules/f&b-management/track-order-frontdesk')}
//             className="btn"
//           >
//             Order Overview
//           </button>
//           <button
//             onClick={() => navigate('/dashboard/core-modules/f&b-management/order-food')}
//             className="btn"
//           >
//             Order Food
//           </button>
//           <button
//             onClick={() => navigate('/dashboard/core-modules/f&b-management/dining-tables')}
//             className="btn"
//           >
//             Dining Tables
//           </button>
//           <button
//             onClick={() => navigate('/dashboard/core-modules/f&b-management/track-order-kitchen')}
//             className="btn font-bold text-black"
//           >
//             Kitchen Display System(KDS)
//           </button>
//           <button
//             onClick={() => navigate('/dashboard/core-modules/f&b-management/order-invoicing')}
//             className="btn text-gray-600 hover:text-black"
//           >
//             Billing & Invoicing
//           </button>
//         </div>
//       </header>

//       <div className="text-center mb-6 mt-8">
//         <h1 className="text-3xl font-bold text-gray-800 mb-1">Kitchen Display System</h1>
//         <p className="text-sm font-poppins font-semibold text-center mb-1 text-gray-600">
//           Manage incoming orders, update preparation status, and coordinate kitchen operations in
//           real time.
//         </p>
//         <hr className="border border-gray-400 mt-1 w-64 mx-auto mb-7" />
//       </div>

//       {/* Stats Cards */}
//       <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
//         <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
//           <div className="flex items-center justify-between">
//             <div>
//               <p className="text-sm text-gray-500">Total Orders</p>
//               <p className="text-2xl font-semibold text-gray-800">{filteredOrders.length}</p>
//             </div>
//             <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
//               <FaClipboardList className="text-gray-600" />
//             </div>
//           </div>
//         </div>
//         <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
//           <div className="flex items-center justify-between">
//             <div>
//               <p className="text-sm text-gray-500">Pending</p>
//               <p className="text-2xl font-semibold text-amber-600">
//                 {filteredOrders.filter((o) => o && o.status === 'pending').length}
//               </p>
//             </div>
//             <div className="w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center">
//               <FaClock className="text-amber-600" />
//             </div>
//           </div>
//         </div>
//         <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
//           <div className="flex items-center justify-between">
//             <div>
//               <p className="text-sm text-gray-500">In Kitchen</p>
//               <p className="text-2xl font-semibold text-indigo-600">
//                 {filteredOrders.filter((o) => o && o.status === 'preparing').length}
//               </p>
//             </div>
//             <div className="w-10 h-10 rounded-full bg-indigo-50 flex items-center justify-center">
//               <FaHourglassHalf className="text-indigo-600" />
//             </div>
//           </div>
//         </div>
//         <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
//           <div className="flex items-center justify-between">
//             <div>
//               <p className="text-sm text-gray-500">Ready to Serve</p>
//               <p className="text-2xl font-semibold text-emerald-600">
//                 {filteredOrders.filter((o) => o && o.status === 'ready').length}
//               </p>
//             </div>
//             <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center">
//               <FaCheckCircle className="text-emerald-600" />
//             </div>
//           </div>
//         </div>
//       </div>

//       {/* Tabs */}
//       <div className="border-b border-gray-200 mb-4">
//         <div className="flex flex-wrap space-x-1">
//           {tabs.map((tab) => {
//             const Icon = tab.icon;
//             const isActive = selectedTab === tab.id;
//             return (
//               <button
//                 key={tab.id}
//                 onClick={() => {
//                   setSelectedTab(tab.id);
//                   setCurrentPage(1);
//                 }}
//                 className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
//                   isActive
//                     ? 'border-b-2 border-gray-800 text-gray-800'
//                     : 'text-gray-500 hover:text-gray-700'
//                 }`}
//               >
//                 <Icon size={14} />
//                 <span>{tab.label}</span>
//                 <span
//                   className={`text-xs px-1.5 py-0.5 rounded-full ${
//                     isActive ? 'bg-gray-100 text-gray-700' : 'bg-gray-100 text-gray-500'
//                   }`}
//                 >
//                   {tab.count}
//                 </span>
//               </button>
//             );
//           })}
//         </div>
//       </div>

//       {/* Search and Filters */}
//       <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
//         <div className="relative w-80">
//           <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
//           <input
//             type="text"
//             placeholder="Search by room, table, or parcel number..."
//             value={searchQuery}
//             onChange={(e) => {
//               setSearchQuery(e.target.value);
//               setCurrentPage(1);
//             }}
//             className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-gray-300"
//           />
//         </div>
//         <div className="flex gap-2">
//           <select
//             value={filterType}
//             onChange={(e) => {
//               setFilterType(e.target.value);
//               setCurrentPage(1);
//             }}
//             className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white text-gray-700"
//           >
//             <option value="all">All Types</option>
//             <option value="room">Room Service</option>
//             <option value="dining">Dine In</option>
//             <option value="parcel">Parcel</option>
//           </select>
//           <select
//             value={filterStatus}
//             onChange={(e) => {
//               setFilterStatus(e.target.value);
//               setCurrentPage(1);
//             }}
//             className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white text-gray-700"
//           >
//             <option value="all">All Status</option>
//             <option value="pending">Pending</option>
//             <option value="accepted">Accepted</option>
//             <option value="preparing">Preparing</option>
//             <option value="ready">Ready</option>
//             <option value="served">Served</option>
//             <option value="completed">Completed</option>
//           </select>
//           <select
//             value={filterDate}
//             onChange={(e) => {
//               setFilterDate(e.target.value);
//               setCurrentPage(1);
//             }}
//             className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white text-gray-700"
//           >
//             <option value="today">Today's Orders</option>
//             <option value="all">All Orders</option>
//           </select>
//         </div>
//       </div>

//       {/* Orders Grid */}
//       {loading ? (
//         <div className="flex justify-center items-center py-12">
//           <FaSpinner className="animate-spin text-gray-400 text-2xl" />
//         </div>
//       ) : currentItems.length === 0 ? (
//         <div className="bg-white rounded-xl border border-gray-200 p-12 text-center shadow-sm">
//           <div className="max-w-md mx-auto">
//             <div className="w-24 h-24 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
//               <FaClipboardList className="text-gray-300 text-4xl" />
//             </div>
//             <h3 className="text-xl font-semibold text-gray-900 mb-1">All Orders Complete! ✨</h3>
//             <p className="text-gray-800 text-lg">
//               Kitchen is clean and ready for action, New orders will appear here automatically
//             </p>
//           </div>
//         </div>
//       ) : (
//         <>
//           <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
//             {currentItems.map((order) => {
//               if (!order) return null;
//               const time = formatTime(order.ordered_at);
//               const statusConfig = getStatusConfig(order.status);
//               const StatusIcon = statusConfig.icon;
//               const TypeIcon = getTypeIcon(order.order_type);

//               return (
//                 <div
//                   key={order.id}
//                   onClick={() => setSelectedOrder(order)}
//                   className={`bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer overflow-hidden ${statusConfig.border} border-l-4`}
//                 >
//                   <div className="p-4">
//                     <div className="flex items-start justify-between mb-3">
//                       <div className="flex items-center gap-2">
//                         <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
//                           <TypeIcon className="text-gray-600 text-sm" />
//                         </div>
//                         <div>
//                           <div className="flex items-center gap-2">
//                             <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
//                               {getTypeLabel(order.order_type)}
//                             </span>
//                             <span className="text-xs text-gray-400">•</span>
//                             <span className="text-xs font-mono text-gray-500">
//                               #{order.kot_number || order.id?.slice(0, 8) || 'N/A'}
//                             </span>
//                           </div>
//                           <div className="font-bold text-lg text-gray-800 mt-0.5">
//                             {order.identifier}
//                           </div>
//                         </div>
//                       </div>
//                       <div
//                         className={`flex items-center gap-1.5 px-2 py-1 rounded-full ${statusConfig.bg}`}
//                       >
//                         <StatusIcon className={`${statusConfig.color} text-xs`} />
//                         <span className={`text-xs font-medium ${statusConfig.color}`}>
//                           {statusConfig.label}
//                         </span>
//                       </div>
//                     </div>

//                     <div className="mb-2">
//                       <div className="flex items-center justify-between text-sm">
//                         <span className="text-gray-500">Time:</span>
//                         <span className="text-gray-600">{time}</span>
//                       </div>
//                     </div>

//                     {/* Food Items */}
//                     <div className="mt-3">
//                       <div className="text-sm font-medium text-gray-700 mb-2">Items:</div>
//                       <div className="grid grid-cols-2 gap-3 max-h-40 overflow-y-auto pr-1">
//                         {order.items &&
//                           order.items.map((item, idx) => {
//                             const itemStatusConfig = getItemStatusConfig(item.item_status);
//                             return (
//                               <div key={idx} className="text-sm text-gray-600">
//                                 <div className="flex items-center justify-between gap-2">
//                                   <div className="flex items-center gap-1 flex-1 min-w-0">
//                                     <span className="truncate">{item.name}</span>
//                                     <span className="font-medium text-gray-700 flex-shrink-0">
//                                       ×{item.quantity}
//                                     </span>
//                                   </div>
//                                   {order.order_type !== 'room' && item.item_status && (
//                                     <span
//                                       className={`text-xs px-1.5 py-0.5 rounded-full flex-shrink-0 ${itemStatusConfig.bg} ${itemStatusConfig.color}`}
//                                     >
//                                       {itemStatusConfig.label}
//                                     </span>
//                                   )}
//                                 </div>
//                                 {/* Show special request for this item */}
//                                 {item.special_request && (
//                                   <div className="mt-1 text-xs text-amber-600 bg-amber-50 inline-block px-2 py-0.5 rounded truncate max-w-full">
//                                     <span className="font-medium">Note:</span>{' '}
//                                     {item.special_request}
//                                   </div>
//                                 )}
//                               </div>
//                             );
//                           })}
//                       </div>
//                       {/* Show order-level special request / kitchen notes */}
//                       {(order.special_request || order.kitchen_notes) && (
//                         <div className="mt-3 pt-2 border-t border-gray-100">
//                           <div className="text-xs font-medium text-gray-500 mb-1">
//                             Kitchen Instructions:
//                           </div>
//                           <div className="text-sm text-amber-700 bg-amber-50 p-2 rounded-md">
//                             {order.kitchen_notes || order.special_request}
//                           </div>
//                         </div>
//                       )}
//                     </div>
//                   </div>
//                 </div>
//               );
//             })}
//           </div>

//           {/* Pagination */}
//           {totalPages > 1 && (
//             <div className="flex justify-between items-center mt-6 px-2">
//               <div className="text-sm text-gray-500">
//                 Showing {indexOfFirstItem + 1}–{Math.min(indexOfLastItem, tabFilteredOrders.length)}{' '}
//                 of {tabFilteredOrders.length} orders
//               </div>
//               <div className="flex items-center gap-1">
//                 <button
//                   onClick={handlePreviousPage}
//                   disabled={currentPage === 1}
//                   className="p-2 rounded-md border border-gray-200 bg-white text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
//                 >
//                   <FaArrowLeft size={12} />
//                 </button>

//                 {currentPage > 10 && (
//                   <>
//                     <button
//                       onClick={() => handlePageChange(1)}
//                       className="px-3 py-1 text-sm rounded-md text-gray-600 hover:bg-gray-100"
//                     >
//                       1
//                     </button>
//                     <span className="px-1 text-gray-400">...</span>
//                   </>
//                 )}

//                 {getVisiblePages().map((page) => (
//                   <button
//                     key={page}
//                     onClick={() => handlePageChange(page)}
//                     className={`px-3 py-1 text-sm rounded-md ${
//                       currentPage === page
//                         ? 'bg-gray-800 text-white'
//                         : 'text-gray-600 hover:bg-gray-100'
//                     }`}
//                   >
//                     {page}
//                   </button>
//                 ))}

//                 {currentPage < totalPages - 9 && (
//                   <>
//                     <span className="px-1 text-gray-400">...</span>
//                     <button
//                       onClick={() => handlePageChange(totalPages)}
//                       className="px-3 py-1 text-sm rounded-md text-gray-600 hover:bg-gray-100"
//                     >
//                       {totalPages}
//                     </button>
//                   </>
//                 )}

//                 <button
//                   onClick={handleNextPage}
//                   disabled={currentPage === totalPages}
//                   className="p-2 rounded-md border border-gray-200 bg-white text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
//                 >
//                   <FaArrowRight size={12} />
//                 </button>
//               </div>
//             </div>
//           )}
//         </>
//       )}

//       {/* Order Details Modal */}
//       {selectedOrder && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
//           <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto relative shadow-xl">
//             <button
//               onClick={() => {
//                 setSelectedOrder(null);
//               }}
//               className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 z-10"
//             >
//               <FaTimes className="w-5 h-5" />
//             </button>

//             <div className="p-6 border-b border-gray-100 sticky top-0 bg-white">
//               <div className="flex justify-between items-center mb-2">
//                 <h2 className="text-2xl font-bold text-gray-800">{selectedOrder.identifier}</h2>
//                 <div
//                   className={`flex items-center gap-1.5 px-2 py-1 mt-4 rounded-full ${getStatusConfig(selectedOrder.status).bg}`}
//                 >
//                   {React.createElement(getStatusConfig(selectedOrder.status).icon, {
//                     className: `${getStatusConfig(selectedOrder.status).color} text-xs`,
//                   })}
//                   <span
//                     className={`text-xs font-medium ${getStatusConfig(selectedOrder.status).color}`}
//                   >
//                     {getStatusConfig(selectedOrder.status).label}
//                   </span>
//                 </div>
//               </div>
//               <p className="text-sm text-gray-500">KOT: {selectedOrder.kot_number}</p>
//             </div>

//             <div className="p-6">
//               <h3 className="font-semibold text-gray-700 mb-4">Order Items</h3>

//               {(selectedOrder.special_request || selectedOrder.kitchen_notes) && (
//                 <div className="mb-4 p-3 bg-amber-50 rounded-lg border border-amber-100">
//                   <div className="flex items-center gap-2 text-amber-700 font-medium text-sm mb-1">
//                     <FaClipboardList size={12} />
//                     <span>Kitchen Instructions</span>
//                   </div>
//                   <p className="text-amber-800 text-sm">
//                     {selectedOrder.kitchen_notes || selectedOrder.special_request}
//                   </p>
//                 </div>
//               )}
//               <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
//                 {selectedOrder.items &&
//                   selectedOrder.items.map((item, index) => {
//                     const itemStatusConfig = getItemStatusConfig(item.item_status);
//                     const isTerminal =
//                       item.item_status === 'served' || item.item_status === 'cancelled';
//                     const canUpdate = !isTerminal && selectedOrder.order_type !== 'room';

//                     return (
//                       <div
//                         key={index}
//                         className="border border-gray-100 rounded-lg p-2 bg-gray-50/30"
//                       >
//                         <div className="flex justify-between items-start mb-3">
//                           <div className="flex-1">
//                             <div className="flex items-center gap-2 mb-1">
//                               <span className="font-semibold text-gray-800 text-sm">
//                                 {item.name}
//                               </span>
//                               <span className="font-medium text-gray-600 bg-white px-2 py-0.5 rounded-full text-sm">
//                                 ×{item.quantity}
//                               </span>
//                             </div>
//                             {item.special_request && (
//                               <p className="text-sm text-amber-600 mt-1 bg-amber-50 inline-block px-2 py-1 rounded">
//                                 {item.special_request}
//                               </p>
//                             )}
//                           </div>
//                           <div
//                             className={`px-2 py-1 rounded-full ${itemStatusConfig.bg} ${itemStatusConfig.color} text-xs font-medium`}
//                           >
//                             {itemStatusConfig.label}
//                           </div>
//                         </div>

//                         {canUpdate && (
//                           <div className="flex gap-2 mt-3 pt-3 border-t border-gray-200">
//                             {item.item_status === 'pending' && (
//                               <button
//                                 onClick={() => {
//                                   updateItemStatus(
//                                     selectedOrder.id,
//                                     item.id,
//                                     'accepted',
//                                     selectedOrder.order_type
//                                   );
//                                   setSelectedOrder({
//                                     ...selectedOrder,
//                                     items: selectedOrder.items.map((i) =>
//                                       i.id === item.id ? { ...i, item_status: 'accepted' } : i
//                                     ),
//                                   });
//                                 }}
//                                 className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm font-medium flex items-center gap-2"
//                               >
//                                 <FaCheck size={12} />
//                                 Accept Order
//                               </button>
//                             )}

//                             {item.item_status === 'accepted' && (
//                               <button
//                                 onClick={() => {
//                                   updateItemStatus(
//                                     selectedOrder.id,
//                                     item.id,
//                                     'preparing',
//                                     selectedOrder.order_type
//                                   );
//                                   setSelectedOrder({
//                                     ...selectedOrder,
//                                     items: selectedOrder.items.map((i) =>
//                                       i.id === item.id ? { ...i, item_status: 'preparing' } : i
//                                     ),
//                                   });
//                                 }}
//                                 className="px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors text-sm font-medium flex items-center gap-2"
//                               >
//                                 <FaFire size={12} />
//                                 Start Preparing
//                               </button>
//                             )}

//                             {item.item_status === 'preparing' && (
//                               <button
//                                 onClick={() => {
//                                   updateItemStatus(
//                                     selectedOrder.id,
//                                     item.id,
//                                     'ready',
//                                     selectedOrder.order_type
//                                   );
//                                   setSelectedOrder({
//                                     ...selectedOrder,
//                                     items: selectedOrder.items.map((i) =>
//                                       i.id === item.id ? { ...i, item_status: 'ready' } : i
//                                     ),
//                                   });
//                                 }}
//                                 className="px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors text-sm font-medium flex items-center gap-2"
//                               >
//                                 <FaCheck size={12} />
//                                 Mark Ready
//                               </button>
//                             )}

//                             {item.item_status === 'ready' && (
//                               <button
//                                 onClick={() => {
//                                   updateItemStatus(
//                                     selectedOrder.id,
//                                     item.id,
//                                     'served',
//                                     selectedOrder.order_type
//                                   );
//                                   setSelectedOrder({
//                                     ...selectedOrder,
//                                     items: selectedOrder.items.map((i) =>
//                                       i.id === item.id ? { ...i, item_status: 'served' } : i
//                                     ),
//                                   });
//                                 }}
//                                 className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm font-medium flex items-center gap-2"
//                               >
//                                 <FaPlay size={12} />
//                                 Mark Served
//                               </button>
//                             )}

//                             {(item.item_status === 'pending' ||
//                               item.item_status === 'accepted' ||
//                               item.item_status === 'preparing') && (
//                               <button
//                                 onClick={() => {
//                                   updateItemStatus(
//                                     selectedOrder.id,
//                                     item.id,
//                                     'cancelled',
//                                     selectedOrder.order_type
//                                   );
//                                   setSelectedOrder({
//                                     ...selectedOrder,
//                                     items: selectedOrder.items.map((i) =>
//                                       i.id === item.id ? { ...i, item_status: 'cancelled' } : i
//                                     ),
//                                   });
//                                 }}
//                                 className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm font-medium flex items-center gap-2"
//                               >
//                                 <FaBan size={12} />
//                                 Cancel Item
//                               </button>
//                             )}
//                           </div>
//                         )}
//                       </div>
//                     );
//                   })}
//               </div>
//             </div>

//             {/* Room Order Buttons */}
//             {selectedOrder.order_type === 'room' && (
//               <div className="p-6 border-t border-gray-100 flex gap-3">
//                 {selectedOrder.status === 'pending' && (
//                   <button
//                     onClick={() => {
//                       updateOrderStatus(selectedOrder.id, 'accepted', selectedOrder.order_type);
//                       setSelectedOrder({
//                         ...selectedOrder,
//                         status: 'accepted',
//                       });
//                     }}
//                     className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm font-medium"
//                   >
//                     Accept Order
//                   </button>
//                 )}

//                 {selectedOrder.status === 'accepted' && (
//                   <button
//                     onClick={() => {
//                       updateOrderStatus(selectedOrder.id, 'preparing', selectedOrder.order_type);
//                       setSelectedOrder({
//                         ...selectedOrder,
//                         status: 'preparing',
//                       });
//                     }}
//                     className="flex-1 px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors text-sm font-medium"
//                   >
//                     Start Preparing
//                   </button>
//                 )}

//                 {selectedOrder.status === 'preparing' && (
//                   <button
//                     onClick={() => {
//                       updateOrderStatus(selectedOrder.id, 'ready', selectedOrder.order_type);
//                       setSelectedOrder({ ...selectedOrder, status: 'ready' });
//                     }}
//                     className="flex-1 px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors text-sm font-medium"
//                   >
//                     Mark Ready
//                   </button>
//                 )}

//                 {selectedOrder.status === 'ready' && (
//                   <button
//                     onClick={() => {
//                       updateOrderStatus(selectedOrder.id, 'delivered', selectedOrder.order_type);
//                       setSelectedOrder({
//                         ...selectedOrder,
//                         status: 'delivered',
//                       });
//                       setTimeout(() => setSelectedOrder(null), 1000);
//                     }}
//                     className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm font-medium"
//                   >
//                     Mark Delivered
//                   </button>
//                 )}

//                 {/* Cancel button - visible for pending, accepted, preparing, ready */}
//                 {(selectedOrder.status === 'pending' ||
//                   selectedOrder.status === 'accepted' ||
//                   selectedOrder.status === 'preparing' ||
//                   selectedOrder.status === 'ready') && (
//                   <button
//                     onClick={() => {
//                       updateOrderStatus(selectedOrder.id, 'cancelled', selectedOrder.order_type);
//                       setSelectedOrder({
//                         ...selectedOrder,
//                         status: 'cancelled',
//                       });
//                       setTimeout(() => setSelectedOrder(null), 1000);
//                     }}
//                     className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm font-medium"
//                   >
//                     Cancel Order
//                   </button>
//                 )}
//               </div>
//             )}
//           </div>
//         </div>
//       )}

//       <ToastContainer position="top-right" />
//     </div>
//   );
// };

// export default TrackOrderKitchen;
